import { useEffect } from 'react'
import { Pane, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet.markercluster'
import { useTranslation } from 'react-i18next'
import { getCityName } from '../../utils/coordinates'
import {
  CityMarker,
  CLUSTER_CITIES_MIN_ZOOM,
  MOTIF_MARKER_PANE,
  MOTIF_MARKER_PANE_Z_INDEX,
  MotifMarkerData,
} from './mapConfig'
import {
  createClusterIcon,
  createMotifIcon,
  guardClusterAutoUnspiderfy,
  setSpiderfyVisible,
} from './mapMarkers'
import { useMotifMarkerSelect } from './useMotifMarkerSelect'
import { installStaggeredSpiderfy, spiderfyShapePositions } from './spiderfyAnimation'

installStaggeredSpiderfy()

interface MotifClusterLayerProps {
  markers: MotifMarkerData[]
  onSelect: (motifId: number) => void
}

/** Headless component that renders the clustered motif markers onto the map. */
const MotifClusterLayer = ({ markers, onSelect }: MotifClusterLayerProps) => {
  const map = useMap()
  const { i18n } = useTranslation()
  const isEnglish = i18n.language.startsWith('en')

  const { bindMotifSelect, isAutoUnspiderfySuppressed } = useMotifMarkerSelect(onSelect)

  useEffect(() => {
    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      showCoverageOnHover: false,
      // layout + staggered reveal are handled in spiderfyAnimation.ts
      spiderfyShapePositions,
      spiderLegPolylineOptions: {
        weight: 0,
        opacity: 0,
        className: 'leaflet-cluster-spider-leg',
      },
      iconCreateFunction: cluster =>
        createClusterIcon(cluster, map.getZoom() >= CLUSTER_CITIES_MIN_ZOOM),
    })

    guardClusterAutoUnspiderfy(clusterGroup, isAutoUnspiderfySuppressed)

    markers.forEach(data => {
      const cityName = getCityName(isEnglish ? data.motif?.EN_settlement : data.motif?.settlement)
      const marker: CityMarker = L.marker([data.coordinate.lat, data.coordinate.lng], {
        icon: createMotifIcon(data, cityName),
        // sit in the motif pane (above the tooltip pane) so a scaled-up pin covers
        // the settlement labels; riseOnHover orders pins against each other in it.
        pane: MOTIF_MARKER_PANE,
        riseOnHover: true,
        riseOffset: 2000,
      })
      marker.cityName = cityName
      bindMotifSelect(marker, data)
      clusterGroup.addLayer(marker)
    })

    const handleSpiderfy = (event: L.MarkerClusterSpiderfyEvent) =>
      setSpiderfyVisible(map, event, true)
    const handleUnspiderfy = (event: L.MarkerClusterSpiderfyEvent) =>
      setSpiderfyVisible(map, event, false)
    // while a cluster is open its badge is an ✕ (styled.ts) — clicking it closes
    const openCluster = () =>
      (clusterGroup as unknown as { _spiderfied?: L.MarkerCluster })._spiderfied
    const handleClusterClick = (event: L.LeafletEvent) => {
      const cluster = (event as L.LeafletEvent & { layer?: L.Layer }).layer
      if (cluster && openCluster() === cluster) (cluster as L.MarkerCluster).unspiderfy()
    }
    // the cluster badge's city list depends on zoom (CLUSTER_CITIES_MIN_ZOOM)
    const refreshClusterLabels = () => clusterGroup.refreshClusters()

    map.addLayer(clusterGroup)
    map.on('zoomend', refreshClusterLabels)
    clusterGroup.on('spiderfied', handleSpiderfy)
    clusterGroup.on('unspiderfied', handleUnspiderfy)
    clusterGroup.on('clusterclick', handleClusterClick)

    return () => {
      map.off('zoomend', refreshClusterLabels)
      clusterGroup.off('spiderfied', handleSpiderfy)
      clusterGroup.off('unspiderfied', handleUnspiderfy)
      clusterGroup.off('clusterclick', handleClusterClick)
      map.getContainer().classList.remove('spiderfying')
      map.removeLayer(clusterGroup)
    }
  }, [map, markers, isEnglish, bindMotifSelect, isAutoUnspiderfySuppressed])

  // motif pins live in their own pane, stacked above the tooltip pane so a
  // hovered, scaled-up pin covers the settlement labels instead of sliding under
  return <Pane name={MOTIF_MARKER_PANE} style={{ zIndex: MOTIF_MARKER_PANE_Z_INDEX }} />
}

export default MotifClusterLayer
